/**
 * Step 7 — Sign & Spark.
 *
 * This step owns the climactic UX of the product: user clicks the
 * Spark button, we call `createGi` (real Ed25519 + SHA-256 in the
 * browser), and the SparkMoment animation plays. When the work
 * completes the user sees their new GI card, downloads the `.gi`
 * file, and returns to the Vault.
 *
 * The actual sign call is quick (<50ms) — we deliberately hold the
 * "signing" stage for at least 900ms so the animation has room to
 * breathe. Users with `prefers-reduced-motion` skip that pacing and
 * see the result instantly.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { SignModeToggle, getStoredSignMode, type SignMode } from '@/components/SignModeToggle';
import type { GiBundle, GiSummary, SparkDraft, SparkOutput, VaultSnapshot } from '@/api/types';
import { spark } from '@/api/endpoints/spark';
import { apiFetch } from '@/api/http';
import { vaultKeys } from '@/hooks/useVault';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { downloadBytes } from '@/utils/download';

import {
  parametersFromDraft,
  portsFromDraft,
  useSparkStore,
} from '../store';
import { SparkMoment, type SparkStage } from '../components/SparkMoment';
import { WizardFooter } from './Step1Basic';

export function Step7Sign({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('spark');
  const state = useSparkStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const reduced = useReducedMotion();

  const [stage, setStage] = useState<SparkStage>('idle');
  const [bundle, setBundle] = useState<GiBundle | null>(null);
  const [bundleBytes, setBundleBytes] = useState<Uint8Array | null>(null);
  const [signMode, setSignMode] = useState<SignMode>(() => getStoredSignMode('spark'));

  const create = useMutation({
    mutationFn: async (): Promise<SparkOutput> => {
      const draft: SparkDraft = {
        name: state.name,
        description: state.description,
        version: state.version,
        domain: state.domain,
        author_name: state.author_name,
        inputs: portsFromDraft(state.input_ports),
        outputs: portsFromDraft(state.output_ports),
        parameters: parametersFromDraft(state.parameters),
        wasm_hash: state.wasm_hash,
        source_hash: null,
      };
      return spark.createGi(
        draft,
        state.wasm_bytes ?? undefined,
      );
    },
    onSuccess: (out) => {
      setBundle(out.bundle);
      setBundleBytes(out.bundle_bytes);

      // Optimistically prepend the new GI to the vault snapshot so
      // navigating to /vault shows it immediately alongside ALL
      // previously-signed entries — without waiting for the server
      // refetch to complete.  This prevents the "only the new file
      // shows" bug: the old entries stay in the cache; the refetch
      // that follows will reconcile with the server's authoritative
      // list once it arrives.
      const prev = qc.getQueryData<VaultSnapshot>(vaultKeys.snapshot);
      if (prev && out.bundle) {
        const newGi: GiSummary = bundleToGiSummary(out.bundle);
        qc.setQueryData<VaultSnapshot>(vaultKeys.snapshot, {
          ...prev,
          gis: [newGi, ...prev.gis],
          stats: {
            ...prev.stats,
            total_gis: prev.gis.length + 1,
            total_signatures: prev.stats.total_signatures + 1,
          },
          activity: [
            { kind: 'sparked', root_id: newGi.root_id, name: newGi.name, at: newGi.created_at },
            ...prev.activity,
          ],
        });
      }

      // Still invalidate so the server's authoritative list (which
      // includes ALL the user's GIs, not just the current session's)
      // replaces the optimistic entry once the refetch completes.
      void qc.invalidateQueries({ queryKey: vaultKeys.snapshot });

      // Embedded proof: non-blocking enhancement. Failure does not
      // affect the main Spark result.
      if (signMode === 'embedded' && out.bundle_bytes && out.bundle) {
        void embedProofAsync(out.bundle_bytes, out.bundle.manifest.name, out.bundle.manifest.root_id);
      }
    },
  });

  // Drive the stage machine from mutation state + a paced delay so
  // the animation feels intentional, not jittery.
  useEffect(() => {
    if (!create.isSuccess) return;
    if (reduced) {
      setStage('done');
      return;
    }
    // We're already in "signing". Advance to "revealing" after the
    // burst fires, then to "done" once the card lands.
    const reveal = window.setTimeout(() => setStage('revealing'), 900);
    const done = window.setTimeout(() => setStage('done'), 1800);
    return () => {
      window.clearTimeout(reveal);
      window.clearTimeout(done);
    };
  }, [create.isSuccess, reduced]);

  const handleSpark = () => {
    setStage('signing');
    create.mutate();
  };

  const handleDownload = () => {
    if (!bundle || !bundleBytes) return;
    const safe = bundle.manifest.name.replace(/[^a-z0-9_.-]/gi, '_');
    downloadBytes(bundleBytes, `${safe}-${bundle.manifest.version}.gi`, 'application/json');
  };

  const handleBackToVault = () => {
    useSparkStore.getState().reset();
    // Navigate to /vault, not / — the home dashboard lives at `/`
    // and only renders the proof feed; the GI grid the user expects
    // (and that the Playwright spec asserts against) is at /vault.
    navigate('/vault');
  };

  if (create.isError) {
    return (
      <div className="flex flex-col gap-5">
        <ErrorState
          title={t('moment.error_title')}
          body={(create.error as Error).message}
          onRetry={() => {
            setStage('idle');
            create.reset();
          }}
        />
        <WizardFooter onBack={onBack}>
          <Button variant="secondary" onClick={() => create.reset()}>
            {t('actions.retry', { ns: 'common' })}
          </Button>
        </WizardFooter>
      </div>
    );
  }

  if (stage === 'idle') {
    return (
      <div className="flex flex-col items-center gap-6 rounded-xl border border-border bg-surface p-10 text-center">
        <p className="text-sm text-ink-muted">{t('moment.ready_body')}</p>
        <div className="w-full">
          <SignModeToggle
            actionType="spark"
            value={signMode}
            onChange={setSignMode}
          />
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSpark}
          leading={<BoltGlyph />}
        >
          {t('moment.cta')}
        </Button>
        <WizardFooter onBack={onBack}>
          <span />
        </WizardFooter>
      </div>
    );
  }

  return (
    <SparkMoment
      stage={stage}
      bundle={bundle}
      onDownload={handleDownload}
      onBackToVault={handleBackToVault}
      onRetry={handleSpark}
    />
  );
}

/** Attempt to embed the proof into the file bytes via the API.
 *  Non-critical: errors are logged but never surfaced to the user. */
async function embedProofAsync(
  bytes: Uint8Array,
  name: string,
  rootId: string,
): Promise<void> {
  try {
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    const file_b64 = btoa(binary);
    await apiFetch<unknown>('/proof/sign-and-embed', {
      method: 'POST',
      body: { file_b64, filename: `${name}.gi`, name },
    });
    localStorage.setItem(`gi.embedded.${rootId}`, '1');
  } catch (err) {
    console.error('[embedded-proof] sign-and-embed failed (non-critical):', err);
  }
}

function BoltGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

/** Convert a Spark bundle into the GiSummary shape expected by the
 *  vault snapshot, so we can optimistically update the cache before
 *  the server refetch arrives. */
function bundleToGiSummary(bundle: GiBundle): GiSummary {
  const { manifest, signed_entity } = bundle;
  return {
    root_id: manifest.root_id,
    version: manifest.version,
    name: manifest.name,
    description: manifest.description,
    domain: manifest.domain,
    author_name: manifest.author.name,
    created_at: manifest.created_at,
    status: ['signed'],
    stats: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
  };
  void signed_entity; // unused in summary; present in bundle for download
}
